import { AutoComplete } from "antd";
import { sinkEvent, useGetView } from "../../helpers/utils";
import {
  faBell,
  faBookmark,
  faBookmarkSlash,
  faChartPie,
  faCity,
  faCog,
  faKey,
  faPersonRunning,
  faSearch,
  faShieldCheck,
  faWrench,
} from "@fortawesome/pro-light-svg-icons";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { EntityConfig } from "../../hooks/useEntity";
import { useTranslation } from "react-i18next";
import useApp from "../../hooks/useApp";
import { useRole } from "../../Auth/useRole";
import Entities from "../../Entities";
import { authorizeLogout, unwrap, usersSetFavourites } from "../../api";
import { trackEvent } from "../../helpers/analytics";
import fls from "../../image/fls.svg";
import useLanguageMenu from "../Topbar/useLanguageMenu";
import { useFavourites } from "../../hooks/userFavourites";

import without from "lodash-es/without";
import union from "lodash-es/union";
import { useHotkeys } from "react-hotkeys-hook";
import { Select } from "antd";
import type { GetRef } from "antd";
import { SubMenuType } from "antd/es/menu/interface";
import TopbarCustomers from "../Topbar/TopbarCustomers";
import { useLocation } from "react-use";
import MenuComponent from "./MenuComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Search style classes
const searchStyleClasses = `
  [&_.search-icon_svg]:!text-[#bfbfbf]
  [&_.search-icon]:text-[#bfbfbf]
`;

interface Props {
  collapsed: boolean;
}

/**
 * Left side menu component
 * The menu items are dynamically populated from the entity system based on the user roles
 * Each entity has a list of roles that are allowed to see the entity
 * @param props
 */
export default function (props: Props) {
  const location = useLocation();
  const { t } = useTranslation();
  const { appData, onAfterLogout } = useApp();
  const { isMobile } = useGetView();
  const { hasAnyRole } = useRole();

  const isCollapsed: boolean = appData?.collapsed ?? false;

  const { favourites, setFavourites } = useFavourites({ enabled: true });

  const selectedCustomer = favourites?.selectedCustomer;
  const sidebarFavourites = favourites?.sidebarFavourites ?? [];
  async function updateFavourites(newSidebarFavourites: string[]) {
    const payload = {
      sidebarFavourites: newSidebarFavourites,
      selectedCustomer,
      id: "",
    };
    setFavourites(payload);
    await unwrap(usersSetFavourites({ body: { data: JSON.stringify(payload) } }));
  }

  async function addItemToPreferences(key: string) {
    trackEvent("menu.favourite", { menuKey: key, added: true });
    return updateFavourites(union(sidebarFavourites, [key]));
  }
  async function removeItemFromPreferences(key: string) {
    trackEvent("menu.favourite", { menuKey: key, added: false });
    return updateFavourites(without(sidebarFavourites, key));
  }

  function filterRights(entityConfig: EntityConfig) {
    const roleView = entityConfig.roleView ?? [];
    const canRender = hasAnyRole(...roleView);
    if (!canRender) {
      return null;
    }
    return !!entityConfig;
  }

  const reports: EntityConfig[] = [
    Entities.transaction,
    Entities.issues,
    {
      key: "map",
      baseKey: "map",
      baseRoute: "map",
      endpoint: "",
      roleView: Entities.container.roleView,
      roleAdmin: Entities.container.roleAdmin,
      name: () => t("Container map"),
      singularName: () => t("Container map"),
    },
    Entities.containerEmptying,
    Entities.transactionCharts,
    Entities.transactionConflicts,
    Entities.issueCharts,
    Entities.endTest,
    Entities.biDashboards,
    Entities.biDashboardsManagement,
    Entities.emptyingDetectionLogs,
  ].filter(filterRights);

  const customers = [Entities.customer, Entities.customerTree, Entities.customerFeatures, Entities.flows].filter(
    filterRights
  );

  const accessControl = [
    Entities.contract,
    Entities.nfcCard,
    Entities.largeList,
    Entities.nfcCardBlacklist,
    Entities.regionCode,
    Entities.regionCodeConfigurationGroup,
    Entities.intervalConfigurationGroup,
    Entities.containersAccessGroup,
  ].filter(filterRights);

  const configuration = [
    Entities.container,
    Entities.address,
    Entities.geozone,
    Entities.fractionConfigurationGroup,
    Entities.hardwareUnit,
    Entities.digiGateway,
    Entities.lockTypeAliases,
    Entities.firmware,
    Entities.product,
    Entities.cardReaderGroup,
    Entities.emptyingDetection,
    Entities.mifareDataReadFrame,
    Entities.containerTypes,
    Entities.containerConfigurationTemplate,
    Entities.hardwareModuleLogs,
    Entities.configComparator,
  ].filter(filterRights);

  const service = [
    Entities.serviceTask,
    Entities.serviceCalls,
    Entities.serviceAppEvents,
    Entities.technician,
    Entities.serviceTaskCategory,
    Entities.serviceTaskCause,
    Entities.serviceTaskSolution,
    Entities.serviceTaskMaterial,
    Entities.systemOperationLogs,
    Entities.hardwareCommands,
    Entities.simCardTermination,
  ].filter(filterRights);

  const socialServices = [Entities.socialServicesRunners, Entities.socialServiceActions].filter(filterRights);

  const wasteManagement = [
    Entities.fillContainers,
    Entities.fillLevelSensor,
    Entities.fillLevelSensorConfigurationGroup,
    Entities.locationTracker,
    Entities.taskList,
    Entities.liftingSystem,
    Entities.truck,
    Entities.truckType,
    Entities.truckBody,
    Entities.truckDepots,
    Entities.wasteYard,
  ].filter(filterRights);

  const security = [Entities.user, Entities.permissionGroup, Entities.devices].filter(filterRights);
  const manage = [Entities.appMessage].filter(filterRights);

  const mobile = useGetView().isMobile;

  const path = location.pathname?.replace("/app/", "").split("/")[0] as string;

  const keyMap = {
    customers,
    accessControl,
    configuration,
    security,
    reports,
    manage,
    wasteManagement,
    service,
    socialServices,
  };

  const openKey: string | undefined = Object.entries(keyMap).find((i) =>
    i[1].find((entConf) => entConf?.baseRoute === path)
  )?.[0];

  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set([openKey as string]));

  useLayoutEffect(() => {
    setOpenKeys(new Set([openKey as string]));
  }, [openKey, location.pathname]);

  useEffect(() => {
    if (isCollapsed) {
      setOpenKeys(new Set([]));
    }
  }, [isCollapsed]);

  const langMenu = useLanguageMenu();

  function getLabel(i: EntityConfig) {
    const isFav = sidebarFavourites.find((fav) => i.key === fav);
    const showFav = !isCollapsed;

    return (
      <div className={"flex-1 flex flex-row justify-between align-center"}>
        <div className={"menu-link-wrapper"}>{i.name(t)}</div>
        {showFav ? (
          <>
            {isFav ? (
              <FontAwesomeIcon
                className={"invisible group-hover:visible"}
                icon={faBookmarkSlash}
                role={"button"}
                size={"lg"}
                title={t("Unpin")}
                onClick={(e) => {
                  sinkEvent(e);
                  removeItemFromPreferences(i.key);
                }}
              />
            ) : (
              <FontAwesomeIcon
                className={"invisible group-hover:visible"}
                icon={faBookmark}
                role={"button"}
                size={"lg"}
                title={t("Pin")}
                onClick={(e) => {
                  sinkEvent(e);
                  addItemToPreferences(i.key);
                }}
              />
            )}
          </>
        ) : null}
      </div>
    );
  }
  function canRenderEntity(i: EntityConfig) {
    const roleView = i.roleView ?? [];
    return hasAnyRole(...roleView);
  }
  function convert(i: EntityConfig) {
    if (!canRenderEntity(i)) return null;
    return {
      key: i.baseKey,
      icon: i.icon,
      title: i.name(t),
      label: getLabel(i),
      link: `/app/${i.baseRoute}`,
    };
  }
  function convertTopLevel(i: EntityConfig) {
    if (!canRenderEntity(i)) return null;

    return {
      key: i.baseKey,
      icon: i.icon,
      title: i.name(t),
      link: `/app/${i.baseRoute}`,
      label: <div className={"group-hover:text-white"}>{i.name(t)}</div>,
    };
  }

  function convertFavourites(i: EntityConfig) {
    if (!canRenderEntity(i)) return null;

    return {
      key: i.baseKey + "_fav",
      icon: i.icon,
      title: i.name(t),
      label: getLabel(i),
      link: `/app/${i.baseRoute}`,
    };
  }
  function mobileItem(i: any) {
    if (!mobile) {
      return null;
    }
    return i;
  }
  function convertSub(i: SubMenuType) {
    if (i.children.length === 0) {
      return null;
    }
    return i;
  }

  const [search, setSearch] = useState("");
  const allMenuItems = [
    ...reports,
    ...customers,
    ...accessControl,
    ...configuration,
    ...service,
    ...wasteManagement,
    ...security,
    ...manage,
    ...socialServices,
    ...[Entities.dashboard, Entities.serviceCockpit], // top level
  ];
  const favouritesItems = allMenuItems.filter((entity) => sidebarFavourites?.includes(entity.key));

  const filteredMenuItems = allMenuItems.filter((entity) => {
    if (search === "") {
      return true;
    }
    return entity.name(t).toLowerCase().includes(search.toLowerCase());
  });

  const menuItems = [
    favouritesItems.length
      ? {
          children: favouritesItems.map(convertFavourites),
          label: t("Bookmarked"),
          key: "favourites",
          icon: <FontAwesomeIcon icon={faBookmark} size={"lg"} />,
        }
      : undefined,
    convertTopLevel(Entities.dashboard),
    convertTopLevel(Entities.serviceCockpit),
    convertSub({
      children: reports.map(convert),
      label: t("Reports"),
      key: "reports",
      icon: <FontAwesomeIcon icon={faChartPie} size={"lg"} />,
    }),
    convertSub({
      children: customers.map(convert),
      label: t("Customers"),
      key: "customers",
      icon: <FontAwesomeIcon icon={faCity} size={"lg"} />,
    }),
    convertSub({
      children: accessControl.map(convert),
      label: t("Access Control"),
      key: "accessControl",
      icon: <FontAwesomeIcon icon={faKey} size={"lg"} />,
    }),
    convertSub({
      children: configuration.map(convert),
      label: t("Configuration"),
      key: "configuration",
      icon: <FontAwesomeIcon icon={faCog} size={"lg"} />,
    }),
    convertSub({
      children: wasteManagement.map(convert),
      label: t("Waste Management"),
      key: "wasteManagement",
      icon: (
        <img
          alt={"fill level"}
          className="fak fa-fls fa-lg group-hover:brightness-0 group-hover:invert menu-white-fa-icon"
          height={22}
          src={fls}
          width={22}
        />
      ),
    }),
    convertSub({
      children: service.map(convert),
      label: t("Service"),
      key: "service",
      icon: <FontAwesomeIcon icon={faWrench} size={"lg"} />,
    }),
    convertSub({
      children: socialServices.map(convert),
      label: t("Social service"),
      key: "socialServices",
      icon: <FontAwesomeIcon icon={faPersonRunning} size={"lg"} />,
    }),
    convertSub({
      children: manage.map(convert),
      label: t("User Engagement"),
      key: "manage",
      icon: <FontAwesomeIcon icon={faBell} size={"lg"} />,
    }),
    convertSub({
      children: security.map(convert),
      label: t("Security"),
      key: "security",
      icon: <FontAwesomeIcon icon={faShieldCheck} size={"lg"} />,
    }),
    mobileItem({
      label: <Link to={"/app/profile"}>{t("Profile")}</Link>,
      key: "profile",
    }),

    mobileItem({
      label: (
        <div
          onClick={async () => {
            await unwrap(authorizeLogout());
            await onAfterLogout();
            navigate("/signin", { replace: true });
          }}
        >
          {t("Logout")}
        </div>
      ),
      key: "logout",
    }),
    mobileItem(langMenu),
  ].filter((i) => i);

  const navigate = useNavigate();
  type SelectRef = GetRef<typeof Select>;

  const ref = useRef<SelectRef>(null);

  useHotkeys("shift + s", () => {
    setTimeout(() => {
      ref.current?.focus();
    }, 100);
  });
  return (
    <>
      <div
        className={`mt-1 gap-3 flex flex-col ${searchStyleClasses}`}
        hidden={isMobile ? false : isCollapsed}
        style={{ width: "90%", marginLeft: "auto", marginRight: "auto" }}
      >
        <div>{isMobile ? <TopbarCustomers /> : null}</div>

        <AutoComplete
          ref={ref}
          className={"w100"}
          defaultActiveFirstOption
          options={filteredMenuItems.map((i: EntityConfig) => ({
            value: i.key,
            label: i.name(t),
            baseRoute: i.baseRoute,
          }))}
          placeholder={t("Search...")}
          prefix={<FontAwesomeIcon className={"search-icon"} icon={faSearch} />}
          showSearch={{
            onSearch: setSearch,
          }}
          suffix={<span className="text-gray-400 text-xs">{t("shift + s")}</span>}
          value={search}
          onChange={(v: any, option: any) => {
            setSearch("");

            if (!option?.baseRoute) {
              return;
            }
            trackEvent("menu.search", { menuKey: option?.value });
            navigate("/app/" + option?.baseRoute);
          }}
        />
      </div>
      <div className={"mt-2"} />
      <MenuComponent
        collapsed={props.collapsed}
        items={menuItems}
        openKeys={openKeys}
        selectedKey={path}
        setOpenKeys={setOpenKeys}
      />
    </>
  );
}
